import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { toKebab } from '@/lib/analytics-slugs'
import type { ConfidenceLabel } from '@/types/story'

/**
 * IndustryConfidenceHeatmap (SP4 PR-2 T22).
 *
 * CSS-grid heatmap showing top-8 industries × 5 confidence levels.
 * Cell colour is fixed per-column (confidence palette); opacity is
 * scaled per-row in a linear 0.12 → 1.0 ramp so each industry's
 * hottest confidence pops relative to its own volume — not the
 * global max.
 *
 * Empty cells (count === 0) render as outlined-only cells using
 * `--chart-empty`, so a sparse industry doesn't visually claim
 * a full palette-coloured square.
 *
 * Click navigates to `/stories?industry=<slug>&confidence=<slug>` —
 * both filters combine at the destination list page.
 */

const CONFIDENCE_ORDER: ConfidenceLabel[] = [
  'established',
  'corroborated',
  'reported',
  'single_source',
  'unverified',
]

const COLOUR: Record<ConfidenceLabel, string> = {
  established: 'var(--chart-success-strong)',
  corroborated: 'var(--chart-success)',
  reported: 'var(--chart-caution)',
  single_source: 'var(--chart-warning)',
  unverified: 'var(--chart-muted)',
}

const LABEL: Record<ConfidenceLabel, string> = {
  established: 'Established',
  corroborated: 'Corroborated',
  reported: 'Reported',
  single_source: 'Single-source',
  unverified: 'Unverified',
}

const MAX_ROWS = 8
const MIN_OPACITY = 0.12
const OPACITY_RANGE = 1 - MIN_OPACITY

export interface IndustryConfidenceHeatmapProps {
  data: Array<{ industry: string; counts: Partial<Record<ConfidenceLabel, number>> }>
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

function rowTotal(counts: Partial<Record<ConfidenceLabel, number>>): number {
  return CONFIDENCE_ORDER.reduce((sum, label) => sum + (counts[label] ?? 0), 0)
}

function rowMax(counts: Partial<Record<ConfidenceLabel, number>>): number {
  return CONFIDENCE_ORDER.reduce(
    (max, label) => Math.max(max, counts[label] ?? 0),
    0,
  )
}

export function IndustryConfidenceHeatmap({
  data,
  isLoading,
  isError,
  onRetry,
}: IndustryConfidenceHeatmapProps) {
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Industry × confidence</h3>
        <p className="text-xs text-muted-foreground mb-3">Coverage by article industry tag — not victim-sector targeting.</p>
        <Skeleton
          data-testid="sp4-industry-skeleton"
          className="h-64 w-full"
        />
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Industry × confidence</h3>
        <p className="text-xs text-muted-foreground mb-3">Coverage by article industry tag — not victim-sector targeting.</p>
        <ErrorState
          title="Couldn't load industry data."
          onRetry={onRetry}
        />
      </Card>
    )
  }

  // Empty if there is no data at all or every row has zero total.
  const populated = data.filter((row) => rowTotal(row.counts) > 0)
  if (populated.length === 0) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Industry × confidence</h3>
        <p className="text-xs text-muted-foreground mb-3">Coverage by article industry tag — not victim-sector targeting.</p>
        <p className="text-sm text-muted-foreground">
          No industry data in the selected range.
        </p>
      </Card>
    )
  }

  // Sort by total desc, take top MAX_ROWS.
  const rows = [...populated]
    .sort((a, b) => rowTotal(b.counts) - rowTotal(a.counts))
    .slice(0, MAX_ROWS)

  return (
    <Card className="p-4">
      <h3 className="text-sm font-semibold mb-3">Industry × confidence</h3>
      <p className="text-xs text-muted-foreground mb-3">Coverage by article industry tag — not victim-sector targeting.</p>
      <div
        role="group"
        aria-label="Industry by confidence heatmap"
        className="grid gap-1"
        style={{ gridTemplateColumns: '80px repeat(5, 1fr)' }}
      >
        {/* Column headers: aria-only spacer + 5 confidence labels */}
        <span aria-hidden className="text-xs text-muted-foreground" />
        {CONFIDENCE_ORDER.map((label) => (
          <span
            key={`col-${label}`}
            className="text-[10px] text-muted-foreground text-center truncate"
            title={LABEL[label]}
          >
            {LABEL[label]}
          </span>
        ))}

        {/* Rows: industry label + 5 cells */}
        {rows.map((row) => {
          const maxInRow = rowMax(row.counts)
          return (
            <Row
              key={row.industry}
              industry={row.industry}
              counts={row.counts}
              maxInRow={maxInRow}
              onCellClick={(industry, label) =>
                navigate(
                  `/stories?industry=${toKebab(industry)}&confidence=${toKebab(label)}`,
                )
              }
            />
          )
        })}
      </div>
    </Card>
  )
}

interface RowProps {
  industry: string
  counts: Partial<Record<ConfidenceLabel, number>>
  maxInRow: number
  onCellClick: (industry: string, label: ConfidenceLabel) => void
}

function Row({ industry, counts, maxInRow, onCellClick }: RowProps) {
  return (
    <>
      <span className="text-xs text-muted-foreground truncate py-1" title={industry}>
        {industry}
      </span>
      {CONFIDENCE_ORDER.map((label) => {
        const count = counts[label] ?? 0
        const isEmpty = count === 0
        const opacity = isEmpty
          ? 0
          : MIN_OPACITY + OPACITY_RANGE * (count / Math.max(maxInRow, 1))
        const baseClasses =
          'h-8 w-full rounded-sm transition-[transform,filter] hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        const emptyClasses = 'border border-dashed'
        return (
          <button
            key={`${industry}-${label}`}
            type="button"
            data-testid={`sp4-industry-cell-${industry}-${label}`}
            data-empty={isEmpty ? 'true' : undefined}
            onClick={() => onCellClick(industry, label)}
            aria-label={`${industry} · ${LABEL[label]} — ${count} ${
              count === 1 ? 'story' : 'stories'
            }`}
            style={
              isEmpty
                ? { borderColor: 'var(--chart-empty)' }
                : { backgroundColor: COLOUR[label], opacity }
            }
            className={`${baseClasses} ${isEmpty ? emptyClasses : ''}`}
          />
        )
      })}
    </>
  )
}

// Re-export for downstream convenience
export type { IndustryConfidenceHeatmapProps as Props }
